import React, { useState, createContext, useEffect, useCallback } from 'react';
import { Dayjs } from 'dayjs';
import { useQuery, NetworkStatus } from '@apollo/client';
import { searchMarketplaceSpacesQuery, locationSearchQuery } from '../graphql';
import { debounce } from 'lodash';

import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMapCoord } from '../../../components/GoogleMap';
import { useAuth } from '../../../contexts/AuthContext';
import {
  LocationSearchQuery,
  LocationSearchQueryVariables,
  MarketplaceSpaceSearchQuery,
  MarketplaceSpaceSearchQueryVariables,
} from '@pv/common/graphql/operations';

interface Bounds {
  sw: GoogleMapCoord;
  ne: GoogleMapCoord;
}

// Type alias for edges in the paginated space query
type MarketplaceSpaceEdge = NonNullable<
  NonNullable<
    MarketplaceSpaceSearchQuery['marketplaceSpaceSearch']['paginatedSpaces']['edges']
  >[number]
>;

// Type alias for a node that gets returned in the paginated space query
type SearchNode = NonNullable<MarketplaceSpaceEdge['node']>;

export interface SearchedSpace {
  images: {
    url: string;
  }[];
  seated?: number;
  standing?: number;
  pathname: string;
  name: string;
  slug: string;
  type?: string;
  amenities: {
    id: string;
    name: string;
    slug: string;
    description: string;
    icon: string;
  }[];
  unavailableTimeSlots: {
    id: string;
    date: Dayjs;
    startOffset: number;
    endOffset: number;
  }[];
  venue: {
    type?: string;
    liveAvailability: boolean;
    images?: {
      url: string;
    }[];
    profileBanner?: string;
    name: string;
    slug: string;
    priceCategory: string;
    expressBookSettings: {
      minLeadTimeDays: number;
      maxLeadTimeDays: number;
      minEventDurationHours: number;
      maxEventDurationHours: number;
      minBufferTimeHours: number;
    };
    hoursOfOperation: {
      dayOfWeek: string;
      isOpen: boolean;
      startTime: number;
      endTime: number;
    }[];
    address?: {
      city: string;
      state: string;
      latitude?: number;
      longitude?: number;
    };
    googlePlaceInfo?: {
      rating?: number;
      reviewCount?: number;
      mapsUrl?: string;
    };
  };
}

// This maps the raw search node into something a little more useful for our UI
const mapSearchNodeToSearchedSpace = (space: SearchNode): SearchedSpace => ({
  slug: space.slug,
  images: space.images.filter((image): image is { url: string } => !!image.url),
  seated: space.seated || undefined,
  standing: space.standing || undefined,
  pathname: space.pathname,
  name: space.name,
  type: space.type || undefined,
  amenities: space.amenities || [],
  unavailableTimeSlots: space.unavailableTimeSlots,
  venue: {
    type: space.venue.type || undefined,
    liveAvailability: space.venue.liveAvailability,
    name: space.venue.name,
    slug: space.venue.slug,
    priceCategory: space.venue.priceCategory,
    expressBookSettings: space.venue.expressBookSettings,
    hoursOfOperation: space.venue.hoursOfOperation,
    address: {
      city: space.venue.address?.city || '',
      state: space.venue.address?.state || '',
      latitude: space.venue.address?.latitude || undefined,
      longitude: space.venue.address?.longitude || undefined,
    },
    googlePlaceInfo: {
      rating: space.venue.googlePlaceInfo?.rating || undefined,
      reviewCount: space.venue.googlePlaceInfo?.reviewCount || undefined,
      mapsUrl: space.venue.googlePlaceInfo?.mapsUrl || undefined,
    }
  },
});

const defaultCity = 'San Francisco, CA';

// If there is a city in the URL, use it
// Otherwise, if the guest has a city and state, use it
// Otherwise, use the default city
export const useCity = () => {
  const { guest } = useAuth();
  const params = useParams();
  const cityParam = params.city;
  const city = cityParam
    ? cityParam.replace(/-/g, ' ')
    : guest?.address?.city && guest?.address?.state
      ? `${guest.address.city}, ${guest.address.state}`
      : defaultCity;

  return city;
};

const SearchResultsContext = createContext<{
  spaces: SearchedSpace[];
  loading: boolean;
  locationQuery: string;
  handleLocationQueryChange: (locationQuery: string) => void;
  totalSpaceCount: number;
  hasMore: boolean;
  loadMore: () => void;
  currentBounds: Bounds | null;
  handleBoundsChange: (bounds: Bounds) => void;
  isFetchingMore: boolean;
  isMapControlled: boolean;
  viewport: Bounds | null;
}>({
  spaces: [],
  loading: false,
  locationQuery: '',
  handleLocationQueryChange: (locationQuery: string) => {},
  totalSpaceCount: 0,
  loadMore: () => {},
  hasMore: false,
  currentBounds: null,
  handleBoundsChange: () => {},
  isFetchingMore: false,
  isMapControlled: false,
  viewport: null,
});

export const SearchResultsProvider = ({
  children,
}: {
  children: JSX.Element[] | JSX.Element;
}) => {
  const navigate = useNavigate();

  const [currentBounds, setCurrentBounds] = useState<Bounds | null>(null);
  const [isMapControlled, setIsMapControlled] = useState(false);
  const [viewport, setViewport] = useState<Bounds | null>(null);
  const [allSpaces, setAllSpaces] = useState<SearchedSpace[]>([]);
  const { guest } = useAuth();
  const city = useCity();

  const [locationQuery, setLocationQuery] = useState<string>(city);
  // Location search query - only runs when user searches by location string
  const { data: locationData, loading: locationLoading } = useQuery<
    LocationSearchQuery,
    LocationSearchQueryVariables
  >(locationSearchQuery, {
    variables: { query: locationQuery },
    skip: !locationQuery,
  });

  // Handle location search results
  useEffect(() => {
    if (locationData?.locationSearch?.bounds) {
      const viewport = {
        sw: {
          lat: locationData.locationSearch.bounds.swLat,
          lng: locationData.locationSearch.bounds.swLng,
        },
        ne: {
          lat: locationData.locationSearch.bounds.neLat,
          lng: locationData.locationSearch.bounds.neLng,
        },
      };
      setViewport(viewport);
      setCurrentBounds(viewport);
    }
  }, [locationData]);

  const variables = {
    swLat: Math.floor(currentBounds?.sw.lat || 0),
    swLng: Math.floor(currentBounds?.sw.lng || 0),
    neLat: Math.ceil(currentBounds?.ne.lat || 0),
    neLng: Math.ceil(currentBounds?.ne.lng || 0),
    first: 50,
  };

  const { data, loading, fetchMore, networkStatus } = useQuery<
    MarketplaceSpaceSearchQuery,
    MarketplaceSpaceSearchQueryVariables
  >(searchMarketplaceSpacesQuery, {
    variables: variables,
    skip: !currentBounds,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const spaceData = data?.marketplaceSpaceSearch || null;

  // Update spaces when new data comes in
  useEffect(() => {
    if (spaceData) {
      const newSpaces = (spaceData.paginatedSpaces.edges || []).map(
        (edge) => edge?.node,
      );

      // Add new spaces to existing spaces, avoiding duplicates
      setAllSpaces((prevSpaces) => {
        const spacesById = [...newSpaces, ...prevSpaces]
          .filter((space): space is SearchNode => !!space)
          .reduce<Record<string, SearchedSpace>>((acc, space) => {
            acc[space.slug] = mapSearchNodeToSearchedSpace(space);
            return acc;
          }, {});

        return Object.values(spacesById);
      });
    }
  }, [spaceData]);

  const totalSpaceCount = spaceData?.paginatedSpaces.totalCount || 0;

  const loadMore = () => {
    fetchMore({
      variables: {
        ...variables,
        after: spaceData?.paginatedSpaces.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const newEdges =
          fetchMoreResult.marketplaceSpaceSearch?.paginatedSpaces?.edges || [];
        const existingEdges =
          prev.marketplaceSpaceSearch?.paginatedSpaces?.edges || [];

        const mergedEdges = [...existingEdges, ...newEdges]
          .filter((edge): edge is MarketplaceSpaceEdge => !!edge)
          .reduce<MarketplaceSpaceEdge[]>((acc, edge) => {
            if (!acc.find((e) => e.cursor === edge?.cursor)) {
              acc.push(edge);
            }
            return acc;
          }, []);
        const newPageInfo =
          fetchMoreResult.marketplaceSpaceSearch?.paginatedSpaces.pageInfo;
        return {
          marketplaceSpaceSearch: {
            ...prev.marketplaceSpaceSearch,
            paginatedSpaces: {
              ...prev.marketplaceSpaceSearch?.paginatedSpaces,
              edges: mergedEdges,
              pageInfo: newPageInfo,
              totalCount:
                fetchMoreResult.marketplaceSpaceSearch?.paginatedSpaces
                  .totalCount,
            },
          },
        };
      },
    });
  };

  const handleScroll = debounce(() => {
    if (
      guest &&
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200
    ) {
      loadMore();
    }
  }, 200);

  useEffect(() => {
    if (spaceData?.paginatedSpaces.pageInfo.hasNextPage) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [spaceData, handleScroll]);

  const debouncedBoundsChange = debounce((bounds: Bounds) => {
    setCurrentBounds(bounds);
    setIsMapControlled(true);
  }, 300);

  const handleBoundsChange = (bounds: Bounds) => {
    debouncedBoundsChange(bounds);
  };

  const handleLocationQueryChange = useCallback(
    (query: string) => {
      if (query === '') {
        setLocationQuery(query);
        return;
      }

      const formattedQuery = query.replace(/ /g, '-').replace(/,/g, '');
      const targetPath = `/cities/${formattedQuery}`;

      // Only navigate if we're not already on the target path
      if (window.location.pathname !== targetPath) {
        navigate(targetPath, { replace: false });
        setViewport(null);
        setCurrentBounds(null);
        setIsMapControlled(false);
        const newLocationQuery = query.replace(/,/g, '');
        setLocationQuery(newLocationQuery);
      }
    },
    [navigate],
  );

  useEffect(() => {
    handleLocationQueryChange(city);
  }, [city, handleLocationQueryChange]);

  return (
    <SearchResultsContext.Provider
      value={{
        spaces: allSpaces,
        loading: loading || locationLoading || !currentBounds,
        locationQuery,
        handleLocationQueryChange,
        totalSpaceCount,
        hasMore: spaceData?.paginatedSpaces.pageInfo.hasNextPage ?? false,
        loadMore,
        currentBounds,
        handleBoundsChange,
        isFetchingMore,
        isMapControlled,
        viewport,
      }}
    >
      {children}
    </SearchResultsContext.Provider>
  );
};

export const useSearchResults = () => React.useContext(SearchResultsContext);
